import { TicketRequestType } from 'cmp-client-api';
import { LayoutTypeEnum } from 'swo-ciam-common-react';
import { RoutePathsEnum, RoutePathSuffixEnum } from '@src/core/routing/route-paths.enum';
import { useCallback, useMemo } from 'react';
import { useFlags } from '@unleash/proxy-client-react';
import { RouteConfig } from './types/route-config.interface';
import { CMPResourceGroup } from '../rbac/types/rbac-restriction.enum';
import lazyWithRetry from './utils/lazy-retry.wrapper';
import Flags from '../flags/types/flags.enum';

const requestsCatalogRouteElement = lazyWithRetry(
  () => import('@src/domain/pages/requests/requests.component')
);

export const routes: RouteConfig[] = [
  {
    path: RoutePathsEnum.ServiceRequestDetails,
    element: lazyWithRetry(
      () =>
        import(
          '@src/domain/pages/automation/service-request/service-request-details/service-request-view.component'
        )
    ),
    breadcrumb: 'Request Details',
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.Automation,
  },
  {
    path: RoutePathsEnum.Provisioning,
    element: lazyWithRetry(
      () => import('@src/domain/pages/automation/provisioning/provisioning.component')
    ),
    breadcrumb: 'Request Provisioning',
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.Automation,
  },
  {
    path: RoutePathsEnum.ServiceRequestCreate,
    element: lazyWithRetry(
      () => import('@src/domain/pages/automation/provisioning/provisioning.component')
    ),
    breadcrumb: 'Create Service Request',
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.Automation,
  },
  {
    path: RoutePathsEnum.AuditLogs,
    element: lazyWithRetry(() => import('@src/domain/pages/audit-logs/audit-logs.component')),
    breadcrumb: 'Audit Logs',
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.AuditLogs,
  },
  {
    path: RoutePathsEnum.Automation,
    element: lazyWithRetry(() => import('@src/domain/pages/automation/automation.component')),
    breadcrumb: 'Automation',
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.Automation,
  },
  {
    path: RoutePathsEnum.RequestsDashboard,
    element: requestsCatalogRouteElement,
    breadcrumb: 'Requests',
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.RequestsCatalog,
    flag: Flags.RequestsCatalog,
  },
  {
    path: RoutePathsEnum.SupportSystemHome,
    element: lazyWithRetry(
      () => import('@src/domain/pages/support/tickets/support-system-home.component')
    ),
    breadcrumb: 'New ticket',
    resource: CMPResourceGroup.SupportSystem,
    layoutType: LayoutTypeEnum.Full,
    children: [
      {
        path: RoutePathsEnum.SupportSystemTicketsIndividualPartial,
        element: lazyWithRetry(
          () =>
            import('@src/domain/pages/support/tickets/tickets-dashboard/views/my-tickets.component')
        ),
        routeProps: {
          scope: TicketRequestType.Individual,
        },
        layoutType: LayoutTypeEnum.Full,
        resource: CMPResourceGroup.SupportSystemIndividual,
      },
      {
        path: RoutePathsEnum.SupportSystemTicketsCompany,
        element: lazyWithRetry(
          () =>
            import(
              '@src/domain/pages/support/tickets/tickets-dashboard/views/company-tickets.component'
            )
        ),
        routeProps: {
          scope: TicketRequestType.Company,
        },

        resource: CMPResourceGroup.SupportSystemCompany,
        layoutType: LayoutTypeEnum.Full,
      },
      {
        path: RoutePathsEnum.SupportSystemTicketDetailPartial,
        element: lazyWithRetry(
          () => import('@src/domain/pages/support/tickets/tickets-details/ticket-details.component')
        ),
        layoutType: LayoutTypeEnum.Full,
        resource: CMPResourceGroup.SupportSystemIndividual,
      },
      {
        path: RoutePathsEnum.CasesIndividualPartial,
        element: lazyWithRetry(
          () => import('@src/domain/pages/support/cases/cases-dashboard/views/my-cases.component')
        ),
        resource: CMPResourceGroup.CasesIndividual,
        routeProps: {
          scope: TicketRequestType.Individual,
        },
        layoutType: LayoutTypeEnum.Full,
      },
      {
        path: RoutePathsEnum.CasesCompany,
        element: lazyWithRetry(
          () =>
            import('@src/domain/pages/support/cases/cases-dashboard/views/company-cases.component')
        ),
        resource: CMPResourceGroup.CasesCompany,
        routeProps: {
          scope: TicketRequestType.Company,
        },
        layoutType: LayoutTypeEnum.Full,
      },
      {
        path: RoutePathsEnum.CaseDetailPartial,
        element: lazyWithRetry(
          () => import('@src/domain/pages/support/cases/cases-details/cases-details.component')
        ),
        resource: CMPResourceGroup.CasesIndividual,
        layoutType: LayoutTypeEnum.Full,
      },
      {
        path: RoutePathsEnum.EscalationDetailPartial,
        element: lazyWithRetry(
          () => import('@src/domain/pages/support/cases/cases-details/escalation-details.component')
        ),
        resource: CMPResourceGroup.CasesIndividual,
        layoutType: LayoutTypeEnum.Full,
      },
    ],
  },
  {
    path: RoutePathsEnum.PublisherUpdates,
    element: lazyWithRetry(() => import('@src/core/routing/utils/outlet')),
    breadcrumb: 'Publisher updates',
    resource: CMPResourceGroup.PublisherUpdates,
    layoutType: LayoutTypeEnum.Full,
    children: [
      {
        path: RoutePathsEnum.PublisherUpdatesDashboard,
        element: lazyWithRetry(
          () => import('@src/domain/pages/publisher-updates/table/view/publisher-updates.component')
        ),
        layoutType: LayoutTypeEnum.Full,
      },
      {
        path: RoutePathsEnum.PublisherUpdatesDetails,
        element: lazyWithRetry(
          () =>
            import(
              '@src/domain/pages/publisher-updates/details/general/view/publisher-update-detail.component'
            )
        ),
        layoutType: LayoutTypeEnum.Full,
      },
      {
        path: RoutePathsEnum.PublisherUpdatesDetailsContracts,
        element: lazyWithRetry(
          () =>
            import(
              '@src/domain/pages/publisher-updates/details/contracts/view/publisher-update-detail-contracts.component'
            )
        ),
        layoutType: LayoutTypeEnum.Full,
        flag: Flags.PublisherDetailsTabMyContracts,
        resource: CMPResourceGroup.PublisherUpdateTabsMyContracts,
      },
      {
        path: RoutePathsEnum.PublisherUpdatesDetailsFiles,
        element: lazyWithRetry(
          () =>
            import(
              '@src/domain/pages/publisher-updates/details/files/view/publisher-update-detail-files.component'
            )
        ),
        layoutType: LayoutTypeEnum.Full,
      },
    ],
  },
  {
    path: RoutePathsEnum.Home,
    element: lazyWithRetry(() => import('@src/domain/pages/home/view/home.component')),
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.CmpWithSCU,
  },
  {
    path: RoutePathsEnum.Settings,
    element: lazyWithRetry(() => import('@src/domain/pages/settings/view/settings.component')),
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.CmpWithSCU,
  },
  {
    path: RoutePathsEnum.NotificationSettings,
    element: lazyWithRetry(
      () => import('@src/domain/pages/notification-settings/notification-settings.component')
    ),
    layoutType: LayoutTypeEnum.Full,
    resource: CMPResourceGroup.PublisherUpdates,
    children: [
      {
        path: RoutePathsEnum.NotificationSettingsPublisherUpdates,
        element: lazyWithRetry(
          () =>
            import(
              '@src/domain/pages/notification-settings/publisher-updates/view/publisher-updates.component'
            )
        ),
        layoutType: LayoutTypeEnum.Full,
        resource: CMPResourceGroup.PublisherUpdates,
      },
    ],
  },
  {
    path: RoutePathsEnum.Reports,
    element: lazyWithRetry(
      () => import('@src/domain/pages/reports/reports-page-wrapper.component')
    ),
    resource: CMPResourceGroup.Reports,
    layoutType: LayoutTypeEnum.Full,
    children: [
      {
        path: RoutePathsEnum.ReportsActiveReportsPartial,
        element: lazyWithRetry(() => import('@src/domain/pages/reports/active-reports.component')),
        layoutType: LayoutTypeEnum.Full,
        children: [
          {
            path: RoutePathSuffixEnum.RecordId,
            element: lazyWithRetry(
              () => import('@src/domain/pages/reports/active-report.component')
            ),
            layoutType: LayoutTypeEnum.Full,
          },
        ],
      },
      {
        path: RoutePathSuffixEnum.Empty,
        element: lazyWithRetry(() => import('@src/domain/pages/reports/reports-home.component')),
        layoutType: LayoutTypeEnum.Full,
      },
      {
        path: RoutePathsEnum.ReportsAvailableReportsPartial,
        element: lazyWithRetry(
          () => import('@src/domain/pages/reports/available-reports.component')
        ),
        breadcrumb: 'Available Reports',
        layoutType: LayoutTypeEnum.Full,
      },
    ],
  },
  {
    path: RoutePathsEnum.MyProjects,
    element: lazyWithRetry(
      () => import('@src/domain/pages/my-projects/table/view/my-projects.component')
    ),
    breadcrumb: 'My Projects',
    resource: CMPResourceGroup.MyProjects,
    layoutType: LayoutTypeEnum.Full,
    children: [
      {
        path: RoutePathsEnum.MyProjectsDashboard,
        element: lazyWithRetry(
          () => import('@src/domain/pages/my-projects/table/view/my-projects.component')
        ),
        layoutType: LayoutTypeEnum.Full,
      },
    ],
  },
  {
    path: RoutePathsEnum.Error401,
    element: lazyWithRetry(() => import('@src/core/routing/components/401/error-401.component')),
    resource: CMPResourceGroup.General,
    layoutType: LayoutTypeEnum.None,
  },
  {
    path: RoutePathsEnum.Error403,
    element: lazyWithRetry(() => import('@src/core/routing/components/403/error-403.component')),
    resource: CMPResourceGroup.General,
    layoutType: LayoutTypeEnum.None,
  },
  {
    path: RoutePathsEnum.Error404,
    element: lazyWithRetry(() => import('@src/core/routing/components/404/error-404.component')),
    resource: CMPResourceGroup.General,
    layoutType: LayoutTypeEnum.None,
  },
  {
    path: RoutePathsEnum.Error500,
    element: lazyWithRetry(() => import('@src/core/routing/components/500/error-500.component')),
    resource: CMPResourceGroup.General,
  },
  {
    path: RoutePathSuffixEnum.NotRegistered,
    element: lazyWithRetry(
      () => import('@src/core/routing/components/not-registered/not-registered.component')
    ),
    resource: CMPResourceGroup.General,
    layoutType: LayoutTypeEnum.None,
  },
  {
    path: RoutePathSuffixEnum.Fallback,
    element: lazyWithRetry(() => import('@src/core/routing/components/404/error-404.component')),
    resource: CMPResourceGroup.General,
    layoutType: LayoutTypeEnum.None,
  },
];

const useRoutes = (): RouteConfig[] => {
  const flags = useFlags();

  const enabledFlags: Set<string> = useMemo(
    () => new Set(flags.filter((flag) => flag.enabled).map((flag) => flag.name)),
    [flags]
  );

  const getFilteredRoutes = useCallback(
    (reactRoutes: RouteConfig[] = []) => {
      const newRoutes: RouteConfig[] = [];
      let childrenRoutes: RouteConfig[] = [];
      reactRoutes.forEach((route) => {
        if (!route.flag || enabledFlags.has(route.flag)) {
          childrenRoutes = route?.children ? getFilteredRoutes(route.children) : [];
          newRoutes.push({ ...route, children: childrenRoutes });
        }
      });
      return newRoutes;
    },
    [enabledFlags]
  );
  return useMemo(() => getFilteredRoutes(routes), [getFilteredRoutes]);
};

export default useRoutes;
